import React from "react";
import { I } from "./icons";
import { submitContact } from "./api";

// Lifted out of draft30.jsx.
//
// SupportWidget is the only thing in that module the customer-facing site
// needs, and it needs exactly two imports: icons and submitContact. draft30.jsx
// is 52 KB of operator surfaces — the disputes console (itself dead code), the
// text-moderation queue, the support-ticket inbox and its mock data — and it
// pulls in OperatorLock and the grapheme segmenter behind them. Importing one
// widget from it dragged all of that into the bundle every customer downloads.
//
// This audience is overwhelmingly on phones, often on metered or prepaid data.
// Bytes they did not ask for are a cost they pay, not just a slower paint.

function SupportWidget() {
  // The Submit button was `subject && body && setSent(true)` — pure client-side.
  // Somebody asking where their card is typed it, saw "Ticket received", and
  // nothing left the browser. Worse, the confirmation promised "we'll reply to
  // your email within 4 hours" while the form never asked for an email.
  //
  // /guest/contact already existed and already stored tickets; the console
  // reads them now. This just had to call it.
  const [open, setOpen] = React.useState(false);
  const [sent, setSent] = React.useState(null);   // null | {reference_id}
  const [order, setOrder] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const canSend = email.includes("@") && body.trim().length >= 10;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await submitContact({
        topic: "order",
        email: email.trim(),
        orderId: order.trim() || null,
        message: body.trim(),
      });
      setSent(r);
    } catch (e) {
      // A screen saying "received" when nothing was received is the bug this
      // replaced. Fail visibly instead.
      setError(e.status === 0
        ? "We couldn't reach our servers. Nothing was sent — please email support@carecardexpress.com."
        : e.message);
    } finally {
      setBusy(false);
    }
  };

  const field = {
    padding: '8px 10px', background: 'var(--ink-800)', border: '1px solid var(--ink-600)',
    color: 'var(--ink-100)', borderRadius: 4, fontSize: 12,
  };

  return (
    <div style={{position:'absolute', bottom: 20, right: 20, zIndex: 100}}>
      {open && (
        <div style={{
          position:'absolute', bottom: 60, right: 0, width: 340,
          background:'var(--ink-900)', border:'1px solid var(--ink-600)',
          borderRadius: 12, padding: 18, boxShadow:'0 20px 50px rgba(0,0,0,0.5)',
        }}>
          {!sent ? (
            <>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 14}}>
                <div>
                  <div style={{fontSize: 14, color:'var(--ink-100)', fontWeight: 600, marginBottom: 2}}>Need help?</div>
                  <div style={{fontSize: 11, color:'var(--ink-400)'}}>We reply by email.</div>
                </div>
                <button onClick={() => setOpen(false)} aria-label="Close support widget" style={{background:'none', border:'none', color:'var(--ink-400)', cursor:'pointer'}}>✕</button>
              </div>
              <div style={{display:'flex', flexDirection:'column', gap: 10}}>
                <input
                  placeholder="Order ID (optional)"
                  aria-label="Order ID, optional"
                  value={order} onChange={e=>setOrder(e.target.value)}
                  maxLength={16}
                  style={{...field, fontFamily:'var(--cc-mono)'}}
                />
                <input
                  type="email"
                  placeholder="Your email"
                  aria-label="Your email"
                  value={email} onChange={e=>setEmail(e.target.value)}
                  maxLength={254}
                  style={field}
                />
                <textarea
                  placeholder="How can we help?"
                  aria-label="How can we help?"
                  value={body} onChange={e=>setBody(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  style={{...field, fontFamily:'inherit', resize:'vertical'}}
                />
                {error && <div style={{fontSize: 11.5, color:'var(--danger)', lineHeight: 1.5}}>{error}</div>}
                <button
                  className="btn btn-primary"
                  onClick={submit}
                  style={{fontSize: 13}}
                  disabled={!canSend || busy}
                >
                  <I.Send size={13}/> {busy ? "Sending…" : "Submit"}
                </button>
              </div>
            </>
          ) : (
            <div style={{textAlign:'center', padding:'20px 0'}}>
              <div style={{
                width: 48, height: 48, borderRadius:'50%',
                background:'rgba(78, 163, 122, 0.15)', color:'#7ac69a',
                display:'inline-flex', alignItems:'center', justifyContent:'center',
                marginBottom: 12,
              }}>
                <I.Check size={24} stroke={2.5}/>
              </div>
              <div style={{fontSize: 14, color:'var(--ink-100)', fontWeight: 600, marginBottom: 4}}>We've got it</div>
              <div style={{fontSize: 12, color:'var(--ink-400)', marginBottom: 6}}>
                We'll reply to <strong style={{color:'var(--ink-200)'}}>{email}</strong>.
              </div>
              <div style={{fontSize: 11, color:'var(--ink-400)', fontFamily:'"JetBrains Mono",monospace', marginBottom: 14}}>
                {sent.reference_id}
              </div>
              <button onClick={() => { setSent(null); setOpen(false); setOrder(""); setEmail(""); setBody(""); }} style={{background:'none', border:'none', color:'var(--gold)', cursor:'pointer', fontSize: 12}}>Close</button>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setOpen(!open)}
        aria-label={open ? "Close support widget" : "Open support widget"}
        style={{
          width: 52, height: 52, borderRadius:'50%',
          background:'var(--gold)', color:'#1a1814',
          border:'none', cursor:'pointer',
          boxShadow:'0 6px 20px rgba(0,0,0,0.4)',
          display:'grid', placeItems:'center',
        }}
      >
        {open ? <span style={{fontSize: 18, fontWeight: 600}}>✕</span> : <I.HelpCircle size={22} stroke={2}/>}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Offline fallback page
// ─────────────────────────────────────────────────────────

export { SupportWidget };
